import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePopupFlow } from '../../hooks/usePopupFlow';
import CountryCodeSelect from '../../components/CountryCodeSelect';
import { validateAndFormatPhone } from '../../utils/phoneValidation';
import { detectUserLocationData } from '../../utils/geo';
import OTPVerificationModal from './OTPVerificationModal';
import {
  UserIcon,
  MailIcon,
  PhoneIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  AlertCircleIcon,
  CheckIcon
} from '../../components/Icons';

export default function RegistrationForm({ registrationToken = null, prefill = null }) {
  const auth = useAuth();
  const { handleLoginSuccess } = usePopupFlow();
  const register = auth?.register;
  const registerSocial = auth?.registerSocial;
  const loading = auth?.loading;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [submitting, setSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [registeredIdentities, setRegisteredIdentities] = useState([]);
  const [registrationTokenState, setRegistrationTokenState] = useState(null);
  const [registrationOtps, setRegistrationOtps] = useState(null);

  const isValidEmail = (value) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
  };

  const nameRegex = /^[a-zA-Z\s'-]+$/;

  const locationFetched = useRef(false);

  useEffect(() => {
    if (locationFetched.current) return;
    locationFetched.current = true;
    
    const fetchLocation = async () => {
      const loc = await detectUserLocationData();
      if (loc?.dialCode) {
        setCountryCode(loc.dialCode);
      }
    };
    fetchLocation();
  }, []);


  useEffect(() => {
    if (prefill) {
      setFirstName(prefill.first_name || '');
      setLastName(prefill.last_name || '');
      setEmail(prefill.email || '');
    }
  }, [prefill]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setServerErrors({});
    setSuccessMessage('');

    const tFirst = firstName.trim();
    const tLast = lastName.trim();
    const tEmail = email.trim();
    const rawPhone = phoneNumber.trim();
    const cleanPhoneDigits = rawPhone.replace(/[^\d]/g, '');

    const errors = {};

    // Validate names
    if (!tFirst) {
      errors.firstName = 'First name is required';
    } else if (tFirst.length < 2 || tFirst.length > 50) {
      errors.firstName = 'First name must be between 2 and 50 characters';
    } else if (!nameRegex.test(tFirst)) {
      errors.firstName = 'First name must contain only letters, spaces, hyphens, or apostrophes';
    }

    if (!tLast) {
      errors.lastName = 'Last name is required';
    } else if (tLast.length < 2 || tLast.length > 50) {
      errors.lastName = 'Last name must be between 2 and 50 characters';
    } else if (!nameRegex.test(tLast)) {
      errors.lastName = 'Last name must contain only letters, spaces, hyphens, or apostrophes';
    }

    // Require either Email OR Phone
    if (!tEmail && !rawPhone) {
      errors.general = 'Either an email address or a phone number is mandatory for registration.';
    }

    // Validate email if provided
    if (tEmail && !isValidEmail(tEmail)) {
      errors.email = 'Please enter a valid email address (e.g. name@example.com)';
    }

    // Validate phone if provided (even if email is also provided)
    if (rawPhone && cleanPhoneDigits.length > 0) {
      const hasInvalidChars = /[^\d\s\-()]/.test(rawPhone);
      if (hasInvalidChars) {
        errors.phone = 'Phone number can only contain digits, spaces, and hyphens';
      } else if (!countryCode) {
        errors.phone = 'Please select a valid country code';
      } else {
        const formatted = validateAndFormatPhone(cleanPhoneDigits, countryCode);
        if (!formatted) {
          errors.phone = 'Please enter a valid international phone number for the selected country.';
        }
      }
    }

    // Validate Password
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 12) {
      errors.password = 'Password must be at least 12 characters';
    }

    if (password !== passwordConfirmation) {
      errors.passwordConfirmation = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setServerErrors(errors);
      setSubmitting(false);
      return;
    }

    try {
      const registrationPayload = {
        firstName: tFirst,
        lastName: tLast,
        email: tEmail || undefined,
        countryCode: (rawPhone && cleanPhoneDigits.length > 0) ? countryCode : undefined,
        phone: (rawPhone && cleanPhoneDigits.length > 0) ? cleanPhoneDigits : undefined,
        password,
        passwordConfirmation,
      };

      let result;

      if (registrationToken) {
        result = await registerSocial({
          registrationToken,
          firstName: registrationPayload.firstName,
          lastName: registrationPayload.lastName,
          email: registrationPayload.email,
          countryCode: registrationPayload.countryCode,
          phone: registrationPayload.phone,
          password: registrationPayload.password,
          passwordConfirmation: registrationPayload.passwordConfirmation,
        });

        if (result && result.accessToken) {
          if (typeof window !== 'undefined' && window.opener) {
            await handleLoginSuccess(result.accessToken);
            return;
          }
          setSuccessMessage('Registration completed successfully!');
          window.location.href = '/u/0/dashboard/overview';
          return;
        }

        if (result && result.requiresVerification) {
          setRegisteredIdentities(result.identities || []);
          setRegistrationOtps(result.verificationOtps || null);
          setSuccessMessage('Registration initiated! Please enter the OTP to verify your account.');
          setShowOtpModal(true);
          return;
        }
      } else {
        result = await register(registrationPayload);
        setSuccessMessage('Registration initiated! Please enter the 6-digit OTP code to verify your identity.');
        setRegisteredIdentities(result.identities || []);
        setRegistrationTokenState(result.registration_token);
        setRegistrationOtps(result.verificationOtps || null);
        setShowOtpModal(true);
      }
    } catch (err) {
      console.error('Registration submission error:', err);
      const errorData = err?.response?.data;
      const status = err?.response?.status;
      const errorMsg = errorData?.message || err?.message || 'Registration failed. Please review your details and try again.';

      if (status === 422 && errorData?.errors) {
        const mapped = {};
        Object.entries(errorData.errors).forEach(([key, messages]) => {
          mapped[key] = Array.isArray(messages) ? messages.join(' ') : String(messages);
        });
        setServerErrors(mapped);
      } else if (errorMsg.toLowerCase().includes('email')) {
        setServerErrors({ email: errorMsg });
      } else if (errorMsg.toLowerCase().includes('phone') || errorMsg.toLowerCase().includes('country code')) {
        setServerErrors({ phone: errorMsg });
      } else {
        setServerErrors({ general: errorMsg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerificationComplete = async (result) => {
    setShowOtpModal(false);
    setSuccessMessage('Identity verified successfully! Account is active.');
    if (typeof window !== 'undefined' && window.opener) {
      await handleLoginSuccess(result?.access_token || result?.token);
      return;
    }
    window.location.href = '/u/0/dashboard/overview';
  };

  return (
    <>
      <form onSubmit={handleFormSubmit} className="space-y-4">


        {/* First & Last Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              First Name <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <UserIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                className={`w-full pl-9 pr-3 py-2 text-xs rounded-xl border bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  serverErrors.firstName ? 'border-rose-500' : 'border-slate-300 dark:border-slate-700'
                }`}
                disabled={submitting}
              />
            </div>
            {serverErrors.firstName && (
              <p className="text-[11px] text-rose-500 mt-1">{serverErrors.firstName}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Last Name <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <UserIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                className={`w-full pl-9 pr-3 py-2 text-xs rounded-xl border bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  serverErrors.lastName ? 'border-rose-500' : 'border-slate-300 dark:border-slate-700'
                }`}
                disabled={submitting}
              />
            </div>
            {serverErrors.lastName && (
              <p className="text-[11px] text-rose-500 mt-1">{serverErrors.lastName}</p>
            )}
          </div>
        </div>

        {/* Identity Section Note */}
        <div className="p-2.5 rounded-xl bg-indigo-50/50 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40 text-[11px] text-indigo-700 dark:text-indigo-300">
          <strong>Mandatory Verification:</strong> Please provide either an Email OR a Phone Number below.
        </div>

        {/* Email Address */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Email Address
          </label>
          <div className="relative">
            <MailIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john.doe@example.com"
              className={`w-full pl-9 pr-3 py-2 text-xs rounded-xl border bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                serverErrors.email ? 'border-rose-500' : 'border-slate-300 dark:border-slate-700'
              }`}
              disabled={submitting}
            />
          </div>
          {serverErrors.email && (
            <p className="text-[11px] text-rose-500 mt-1">{serverErrors.email}</p>
          )}
        </div>

        {/* Phone Number with Country Code Dropdown */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Phone Number
          </label>
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-5 sm:col-span-4">
              <CountryCodeSelect
                value={countryCode}
                onChange={(c) => setCountryCode(c.dial_code)}
                disabled={submitting}
                preload={true}
              />
            </div>
            <div className="col-span-7 sm:col-span-8 relative">
              <PhoneIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="771234567"
                className={`w-full pl-9 pr-3 py-2 text-xs rounded-xl border bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  serverErrors.phone ? 'border-rose-500' : 'border-slate-300 dark:border-slate-700'
                }`}
                disabled={submitting}
              />
            </div>
          </div>
          {serverErrors.phone && (
            <p className="text-[11px] text-rose-500 mt-1">{serverErrors.phone}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Password <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <LockIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 12 characters"
              className={`w-full pl-9 pr-10 py-2 text-xs rounded-xl border bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                serverErrors.password ? 'border-rose-500' : 'border-slate-300 dark:border-slate-700'
              }`}
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              {showPassword ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
            </button>
          </div>
          {serverErrors.password && (
            <p className="text-[11px] text-rose-500 mt-1">{serverErrors.password}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Confirm Password <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <LockIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              placeholder="Re-enter password"
              className={`w-full pl-9 pr-10 py-2 text-xs rounded-xl border bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                serverErrors.passwordConfirmation ? 'border-rose-500' : 'border-slate-300 dark:border-slate-700'
              }`}
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              {showConfirmPassword ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
            </button>
          </div>
          {serverErrors.passwordConfirmation && (
            <p className="text-[11px] text-rose-500 mt-1">{serverErrors.passwordConfirmation}</p>
          )}
        </div>

        {/* General Error Alert & Success Message (placed right above submit button for clear visibility) */}
        {serverErrors.general && (
          <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-600 dark:text-rose-400 text-xs flex items-start gap-2 animate-in fade-in duration-200">
            <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{serverErrors.general}</span>
          </div>
        )}

        {successMessage && (
          <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400 text-xs flex items-start gap-2 animate-in fade-in duration-200">
            <CheckIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Submit button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={submitting || loading}
            className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 text-white font-semibold text-xs rounded-xl shadow-md shadow-indigo-600/20 transition-all"
          >
            {submitting || loading ? 'Processing Registration...' : 'Create Account & Verify OTP'}
          </button>
        </div>
      </form>

      {showOtpModal && (
        <OTPVerificationModal
          identities={registeredIdentities}
          registrationToken={registrationTokenState}
          onVerificationComplete={handleVerificationComplete}
          onClose={() => setShowOtpModal(false)}
          registrationOtps={registrationOtps}
        />
      )}
    </>
  );
}